import axios from "axios";
import { computed, unref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { tags } from "@/api/common";
import { queryKeys } from "@/api/queryKeys";
import { useLocationSelector } from "@/composables/location";
import { endpoints } from "@/endpoints";
import useApiStore from "@/stores/api";

import type { MaybeRef } from "@vueuse/core";
import type { VisitorFieldListApiResponse } from "@/api/visitorFields";
import type { VisitorField } from "@/pages/settings/visitors/fields/types";
import { useSubscription } from "@/composables/subscription";

export const useGetVisitorFields = ({
  visitorCategoryId,
  enabled,
  locationGroupId,
}: {
  visitorCategoryId?: MaybeRef<string>;
  enabled?: MaybeRef<boolean>;
  locationGroupId?: MaybeRef<string>;
}) => {
  const apiStore = useApiStore();
  const { selectedLocationGroupPk, isLocationsLoaded } = useLocationSelector();
  const { isFieldsByCategoryEnabled } = useSubscription();

  const mergedFilters = computed(() => {
    const response = {
      locationGroupId: locationGroupId || selectedLocationGroupPk,
      visitorCategoryId,
    };

    if (!isFieldsByCategoryEnabled.value) {
      delete response.visitorCategoryId;
    }

    return response;
  });

  return useQuery({
    queryKey:
      queryKeys[tags.platform.visitorFields].list(mergedFilters).queryKey,
    queryFn: async () => {
      const params = {
        locationGroupPk: unref(mergedFilters.value.locationGroupId),
        visitorCategoryPk: unref(mergedFilters.value.visitorCategoryId),
      };

      if (!params.visitorCategoryPk) {
        delete params.visitorCategoryPk;
      }

      const result = await axios.get<VisitorFieldListApiResponse>(
        apiStore.getEndpoint("platform", endpoints.visitorFields.list),
        { params }
      );

      return result.data;
    },
    enabled: isLocationsLoaded && enabled,
    select: (data) =>
      data.list.map<VisitorField>((field) => ({
        active: field.enabled,
        fieldOptions: field.fieldOptions,
        fieldType: field.fieldType,
        id: field.pk,
        numericId: Number(field.id),
        metadata: field.metadata,
        order: Number(field.order),
        required: field.required,
        settings: field.settings,
        tag: field.fieldType,
        temporary: field.temporary,
        title: field.name,
      })),
  });
};
